/**
 * Client-side integration tests
 * Tests API connections directly from the browser
 */

/**
 * Test Claude API connection
 */
export async function testClaudeAPI(config) {
  const { apiKey } = config

  if (!apiKey) {
    return { ok: false, error: 'API Key not configured' }
  }

  try {
    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': apiKey,
        'anthropic-version': '2023-06-01',
        'anthropic-dangerous-direct-browser-access': 'true'
      },
      body: JSON.stringify({
        model: config.model || 'claude-sonnet-4-6',
        max_tokens: 10,
        messages: [{ role: 'user', content: 'Say "OK" if you can read this.' }]
      })
    })

    if (response.ok) {
      const data = await response.json()
      return {
        ok: true,
        status: response.status,
        message: 'Claude API connected successfully',
        model: data.model,
        response: data.content?.[0]?.text || 'OK'
      }
    } else {
      const error = await response.json().catch(() => ({}))
      return {
        ok: false,
        status: response.status,
        error: error.error?.message || `HTTP ${response.status}`,
        hint: response.status === 401 ? 'Invalid API key' :
              response.status === 403 ? 'API key lacks permissions' :
              response.status === 429 ? 'Rate limited - wait and retry' : ''
      }
    }
  } catch (err) {
    return {
      ok: false,
      error: err.message || 'Network error',
      hint: 'Check your internet connection'
    }
  }
}

/**
 * Test KIE AI API connection
 */
export async function testKieAIAPI(config) {
  const apiKey = String(config?.apiKey || '').trim()
  const baseUrl = String(config?.baseUrl || 'https://api.kie.ai').trim().replace(/\/+$/, '')

  if (!apiKey) {
    return { ok: false, error: 'API Key not configured' }
  }

  try {
    const response = await fetch(`${baseUrl}/api/v1/jobs/getTaskResult?taskId=integration-healthcheck`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${apiKey}`
      }
    })

    const data = await response.json().catch(() => ({}))

    if (response.ok || response.status === 400) {
      return {
        ok: true,
        status: response.status,
        message: 'KIE AI endpoint reachable and key accepted',
        details: data?.msg || data?.message || null
      }
    }

    return {
      ok: false,
      status: response.status,
      error: data?.msg || data?.message || `HTTP ${response.status}`,
      hint: response.status === 401 ? 'Invalid API key' :
        response.status === 403 ? 'Key lacks permissions' : ''
    }
  } catch (err) {
    return {
      ok: false,
      error: err.message || 'Network error',
      hint: 'Check internet access and API base URL'
    }
  }
}

/**
 * Test Shopify API connection
 */
export async function testShopifyAPI(config) {
  const { shopDomain, token } = config

  if (!shopDomain || !token) {
    return { ok: false, error: 'Shop domain and token required' }
  }

  try {
    // Use a simple GraphQL query to test connection
    const response = await fetch(`https://${shopDomain}/admin/api/2024-01/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Access-Token': token
      },
      body: JSON.stringify({
        query: '{ shop { name } }'
      })
    })

    if (response.ok) {
      const data = await response.json()
      if (data.data?.shop?.name) {
        return {
          ok: true,
          status: response.status,
          message: `Connected to ${data.data.shop.name}`,
          shopName: data.data.shop.name
        }
      }
    }

    const error = await response.json().catch(() => ({}))
    return {
      ok: false,
      status: response.status,
      error: error.errors?.[0]?.message || `HTTP ${response.status}`,
      hint: response.status === 401 ? 'Invalid access token' : ''
    }
  } catch (err) {
    // CORS will block this - need server-side test
    return {
      ok: false,
      error: 'Cannot test from browser (CORS)',
      hint: 'Shopify blocks browser requests. Save config and test from server.'
    }
  }
}

/**
 * Test a Google service via the server-side `testIntegration` callable
 * (admin-only; runs the request with the service-account credentials).
 */
async function testViaServerCallable(name, config) {
  try {
    const { getFns } = await import('../firebase')
    const { httpsCallable } = await import('firebase/functions')
    const call = httpsCallable(getFns(), 'testIntegration')
    const { data } = await call({ name, ...(config || {}) })
    if (data?.ok) {
      return {
        ok: true,
        status: data.status,
        message: `${name} connected (server-side test)`,
        latencyMs: data.latencyMs
      }
    }
    return {
      ok: false,
      status: data?.status ?? 0,
      error: data?.bodySnippet || 'Connection failed',
      hint: data?.hint || ''
    }
  } catch (err) {
    return {
      ok: false,
      error: err?.message || 'Server test failed',
      hint: 'Admin role required; check server logs'
    }
  }
}

/**
 * Test Google Drive API connection via server
 */
export function testGoogleDriveAPI(config) {
  return testViaServerCallable('google_drive', config)
}

/**
 * Test Google Sheets API connection via server
 */
export function testGoogleSheetsAPI(config) {
  return testViaServerCallable('google_sheets', config)
}

/**
 * Test Google Calendar API connection via server
 */
export function testGoogleCalendarAPI(config) {
  return testViaServerCallable('google_calendar', config)
}

/**
 * Test Podium API connection
 */
export async function testPodiumAPI(config) {
  const { apiKey, baseUrl } = config

  if (!apiKey) {
    return { ok: false, error: 'API Key not configured' }
  }

  try {
    const response = await fetch(`${baseUrl || 'https://api.podium.com'}/v4/me`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${apiKey}`
      }
    })

    if (response.ok) {
      return {
        ok: true,
        status: response.status,
        message: 'Podium API connected'
      }
    }

    return {
      ok: false,
      status: response.status,
      error: `HTTP ${response.status}`,
      hint: response.status === 401 ? 'Invalid API key' : ''
    }
  } catch (err) {
    return {
      ok: false,
      error: 'Cannot test from browser (CORS)',
      hint: 'Save config - connection will be verified when used.'
    }
  }
}

/**
 * Test Metal Prices API connection
 */
export async function testMetalPricesAPI(config) {
  const { primaryApi, goldapiKey, metalpriceapiKey } = config

  try {
    // Test metals.live (free, no CORS issues typically)
    if (primaryApi === 'metals_live' || !primaryApi) {
      const response = await fetch('https://api.metals.live/v1/spot')
      if (response.ok) {
        const data = await response.json()
        const gold = data.find(m => m.metal === 'gold')
        const silver = data.find(m => m.metal === 'silver')
        return {
          ok: true,
          status: response.status,
          message: 'Metals.live connected',
          prices: {
            gold: gold?.price || null,
            silver: silver?.price || null
          }
        }
      }
    }

    // Test GoldAPI.io
    if (primaryApi === 'goldapi' || goldapiKey) {
      if (!goldapiKey) {
        return { ok: false, error: 'GoldAPI key not configured' }
      }
      const response = await fetch('https://www.goldapi.io/api/XAU/USD', {
        headers: { 'x-access-token': goldapiKey }
      })
      if (response.ok) {
        const data = await response.json()
        return {
          ok: true,
          status: response.status,
          message: 'GoldAPI.io connected',
          prices: { gold: data.price, silver: null }
        }
      }
    }

    // Test MetalPriceAPI
    if (primaryApi === 'metalpriceapi' || metalpriceapiKey) {
      if (!metalpriceapiKey) {
        return { ok: false, error: 'MetalPriceAPI key not configured' }
      }
      const response = await fetch(`https://api.metalpriceapi.com/v1/latest?api_key=${metalpriceapiKey}&base=USD&currencies=XAU,XAG`)
      if (response.ok) {
        const data = await response.json()
        return {
          ok: true,
          status: response.status,
          message: 'MetalPriceAPI connected',
          prices: {
            gold: data.rates?.XAU ? 1 / data.rates.XAU : null,
            silver: data.rates?.XAG ? 1 / data.rates.XAG : null
          }
        }
      }
    }

    return {
      ok: false,
      error: 'No API configured or all APIs failed',
      hint: 'Configure at least one metal price API'
    }
  } catch (err) {
    return {
      ok: false,
      error: err.message || 'Network error',
      hint: 'Check your internet connection or API might be blocked by CORS'
    }
  }
}

/**
 * Run test for a specific integration
 */
export async function runIntegrationTest(name, config) {
  const startTime = Date.now()

  let result
  switch (name) {
    case 'claude':
      result = await testClaudeAPI(config)
      break
    case 'kie_ai':
      result = await testKieAIAPI(config)
      break
    case 'shopify':
      result = await testShopifyAPI(config)
      break
    case 'google_drive':
      result = await testGoogleDriveAPI(config)
      break
    case 'google_sheets':
      result = await testGoogleSheetsAPI(config)
      break
    case 'google_calendar':
      result = await testGoogleCalendarAPI(config)
      break
    case 'podium':
      result = await testPodiumAPI(config)
      break
    case 'metal_prices':
      result = await testMetalPricesAPI(config)
      break
    default:
      result = { ok: false, error: `Unknown integration: ${name}` }
  }

  return {
    ...result,
    latencyMs: Date.now() - startTime,
    testedAt: new Date().toISOString()
  }
}
